import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import MuiButton from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { useHasTwoFactorToken, useLoginWithCode, useSendCode, useRemoveAuthItem } from '@hilma/auth';
import { useForm } from '@hilma/netina2all';

import { useI18n, useNextButtonTarget } from '../../../hooks';
import { Storage, TranslatedI18n } from '../../../types';
import { Button } from '../../../components';

import Code from './Code';
import { FormikValues } from 'formik';

interface LoginDialogProps {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginDialog: React.FC<LoginDialogProps> = ({ open, setOpen }) => {
	const formRef = useRef<ReturnType<typeof useForm> | null>(null);
	const [errorMessage, setErrorMessage] = useState('');

	const removeAuthItem = useRemoveAuthItem();
	const hasTwoFactorToken = useHasTwoFactorToken();
	const loginWithCode = useLoginWithCode();
	const sendCode = useSendCode();
	const form = useForm();

	const [nextButtonTarget] = useNextButtonTarget();

	const pd = useI18n(i18n => i18n.pd);

	const classes = useStyles();

	const phone = useMemo(() => form.values.phone, [form.values.phone]);

	const removeToken = useCallback(() => removeAuthItem<Storage>('lkool'), [removeAuthItem]);

	const onCancel = useCallback(async () => {
		setOpen(false);
		await removeToken();
	}, [removeToken]);

	const onSendCode = useCallback(async () => {
		try {
			const { success, msg } = await sendCode('/api/customer/send-code', {
				username: phone
			});

			if (!success && msg?.data?.key) setErrorMessage(pd.errors[msg.data.key as keyof TranslatedI18n['pd']['errors']]);
		} catch (error) {
			console.error(error);
		}
	}, [sendCode, phone, pd]);

	const onLoginWithCode = useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();
		formRef.current!.handleSubmit();
	}, [formRef.current]);

	const onLoginWithCodeSubmit = useCallback(async (values: FormikValues) => {
		try {
			const { success, msg } = await loginWithCode('/api/customer/login-with-code', values.code);

			if (success) {
				setOpen(false);
				nextButtonTarget?.click();
				return;
			}

			if (msg?.data?.key) formRef.current!.setFieldError('code', pd.errors[msg.data.key as keyof TranslatedI18n['pd']['errors']]);
		} catch (error) {
			console.error(error);
		}
	}, [loginWithCode, pd, formRef.current, nextButtonTarget]);

	useEffect(() => {
		if (!open) return;
		setErrorMessage('');
		removeToken();
	}, [open]);

	return (
		<Dialog open={open} maxWidth="sm" fullWidth onExited={() => removeToken()}>
			<form>
				<DialogContent className={classes.content}>
					<img className={classes.image} src="/lulav-icons/purim-icon.svg" />
					{!hasTwoFactorToken ? (
						<div className={clsx(classes.text, errorMessage && classes.red)}>{errorMessage || pd.phoneExists}</div>
					) : (
						<Code
							phone={phone}
							formRef={formRef}
							onLoginWithCodeSubmit={onLoginWithCodeSubmit}
						/>
					)}
					<div className={classes.actions}>
						<MuiButton
							onClick={onCancel}
							color="primary"
							variant="text"
							classes={{ label: classes.buttonFont }}
						>
							{pd.cancel}
						</MuiButton>
						<Button
							onClick={hasTwoFactorToken ? onLoginWithCode : onSendCode}
							classes={{ root: classes.verify, label: classes.buttonFont }}
							type={hasTwoFactorToken ? 'submit' : undefined}
						>
							{pd.verify}
						</Button>
					</div>
				</DialogContent>
			</form>
		</Dialog>
	);
}

export default LoginDialog;

export const useStyles = makeStyles(theme => ({
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		margin: '30px 7px 5px 7px'
	},

	image: {
		alignSelf: 'center',
		marginBottom: 40,
	},

	text: {
		color: theme.palette.primary.main,
		fontFamily: 'Assistant-SemiBold',
		fontSize: 20,
	},

	red: {
		color: theme.palette.error.main
	},

	actions: {
		display: 'flex',
		alignSelf: 'flex-end',
		alignItems: 'center',
		marginTop: 20,
	},

	verify: {
		marginLeft: 20,
		minHeight: 50,
	},

	buttonFont: {
		fontFamily: 'Assistant-SemiBold',
		fontSize: 23
	}
}))